import { KastWebSdkClient } from "@kalyzee/kast-web-sdk";
import KastAppClient, { UserIdentityOutputDTO, UserOutputDTO } from "@kalyzee/kast-web-sdk/http";
import {
  getImpersonationAllowed,
  getImpersonationTarget,
  setImpersonationAllowed,
  setImpersonationTarget,
  StorageImpersonationTarget,
} from "./storage";
import { getUserDisplayName, getUserLabel, searchUsers, USER_IDENTITY_FIELDS } from "./user";

/**
 * Impersonation ("ghost mode") — the API processes the request AS another user
 * when the caller holds an impersonation right over the target's role. The
 * actor's token is untouched: only the target designation decides who the
 * request runs as, and `KastAppClient.impersonate()` carries it.
 *
 * The websocket is covered too, without anything extra here: its handshake
 * token is minted by an HTTP call (`socket.getToken`) that goes through the same
 * client, so the token itself comes back impersonated.
 */

/** Grants ghost mode over every role. Role-scoped variants also exist server-side. */
export const IMPERSONATE_ANY_PERMISSION = "user:impersonate:any";

/** An email designates an identity, so it resolves user + identity on its own. */
export const isImpersonationTargetEmail = (target: string) => target.includes("@");

export const getImpersonation = getImpersonationTarget;

export const isImpersonating = () => !!getImpersonationTarget();

/**
 * Whether the current account may enter ghost mode. While impersonating,
 * `getMe()` answers for the TARGET — whose permissions are not the actor's —
 * so a stored flag stands in, letting the bar remain available to leave.
 */
export const canImpersonate = () => getImpersonationAllowed();

/**
 * Records whether the actor holds the permission. Only meaningful on a
 * non-impersonated response: while impersonating, the payload describes the
 * target and would clear a flag that is still true for the actor.
 */
export const rememberImpersonationAllowed = (user: UserOutputDTO) => {
  if (isImpersonating()) return;
  setImpersonationAllowed(!!user.permissions?.includes(IMPERSONATE_ANY_PERMISSION));
};

export const clearImpersonation = () => {
  setImpersonationTarget(undefined);
  setImpersonationAllowed(false);
};

/**
 * Mirrors the stored target onto the client. Called once when the client is
 * built, so every request — including the first one — runs as the target.
 * Changing the target goes through a full reload rather than a live switch: the
 * websocket connection, the store and the meeting contexts were all built for
 * the previous identity.
 */
export const applyImpersonation = (client: KastWebSdkClient) => {
  const http = client.getHttpClient();
  if (!http) return;
  const impersonation = getImpersonationTarget();
  if (!impersonation) {
    http.impersonate();
    return;
  }
  // A bare user id resolves through the target's first identity; `identityId`
  // names one explicitly when the user carries several.
  http.impersonate(
    isImpersonationTargetEmail(impersonation.target)
      ? { email: impersonation.target }
      : { userId: impersonation.target, identityId: impersonation.identityId },
  );
};

/**
 * A client that always speaks as the ACTOR, whatever the app is impersonating.
 *
 * The search cannot go through the app's client: while impersonating, its
 * requests run as the target, and a target that cannot list users (a student,
 * say) would answer 403 — leaving no way to pick the next one. Clearing the
 * impersonation on the shared client for the duration of the request would leak
 * onto whatever else is in flight, hence a separate instance.
 *
 * Tokens are re-read on each call: the app's client refreshes them on its own
 * and this one receives no such event.
 */
let actorClient: KastAppClient | undefined;

const getActorHttpClient = (client: KastWebSdkClient) => {
  const http = client.getHttpClient();
  if (!actorClient) actorClient = new KastAppClient(http.getBaseURL());
  actorClient.setTokens(http.getTokens());
  return actorClient;
};

/** Free-text user search, used by the bar to pick a target without knowing its id. */
export const searchImpersonationTargets = (
  client: KastWebSdkClient,
  query: string,
): Promise<UserOutputDTO[]> => searchUsers(getActorHttpClient(client), query);

export interface ImpersonationIdentity {
  id: string;
  type: string;
  label: string;
}

/**
 * The identities a user can be impersonated through. They come populated with
 * the user search — the identity listing route is KADMIN-only, so a plain
 * `user:impersonate:any` holder could not search them separately.
 *
 * Two traps in that payload, both of which silently emptied this list before:
 * the populated identities carry `_id` and **no `id`** (unlike the DTO type,
 * which declares `id`), and **no `type`** either — hence the field-to-type map
 * of `USER_IDENTITY_FIELDS` rather than reading `identity.type`.
 */
export const getImpersonationIdentities = (user: UserOutputDTO): ImpersonationIdentity[] => (
  USER_IDENTITY_FIELDS.flatMap(({ field, type }) => {
    const identity = user[field] as (UserIdentityOutputDTO & { _id?: string }) | undefined;
    const id = identity?._id ?? identity?.id;
    if (!identity || !id) return [];
    return [{ id, type, label: identity.email || identity.username || id }];
  })
);

// Kept under the bar's own names: nothing about them is impersonation-specific.
export const getImpersonationUserName = getUserDisplayName;
export const getImpersonationUserLabel = getUserLabel;

/** Enters ghost mode and reloads the app. */
export const startImpersonation = (target: StorageImpersonationTarget) => {
  setImpersonationTarget(target);
  window.location.reload();
};

export const stopImpersonation = () => {
  setImpersonationTarget(undefined);
  window.location.reload();
};
